// LICENSE_CODE ZON
'use strict';/*jslint ts:true, react: true*/
import React, {Fragment, useCallback, useContext, useEffect, useRef,
    useState} from 'react';
import {Settings_context} from '../../../../hola/pub/settings_ctx';
import {get_myip, IP_ERROR_CODES, use_ip_list} from '../../../../oculus/pub/ui/dashboard/use_ip';
import {use_app_context} from '../../lib/app_context';
import {find_order_by_id} from './utils';
import {WHITELIST_SUCCESS_CODES, use_whitelist} from '../../../../oculus/pub/ui/dashboard/use_whitelist';
import {Button} from '../../../../shared/ui/button';
import {Btn_loader} from './btn_loader';
import {use_screen} from '../../../../hola/pub/lib/screen_util';
import {Segment_input} from '../../../../oculus/pub/ui/dashboard/ip_whitelist';
import cn from 'classnames';
import etask from 'zetask';
import Icon_chevron_down from '../../img/icons/16/chevron_down.svg';
import Icon_check from '../../img/icons/16/check.svg';
import Icon_close from '../../img/icons/16/close.svg';
import Icon_alert from '../../img/icons/16/alert.svg';

import styles from './ip_whitelist.module.less';

const LIST_INIT_HEIGHT = 0;
const FORM_INIT_HEIGHT = 52;
const SUCCESS_MSG_TIMEOUT = 2000;

const error_msg = {
    [IP_ERROR_CODES.INVALID]: 'Invalid number format',
    [IP_ERROR_CODES.USED]: 'Address already added',
};

const use_expand = (ref, value, render_key?, limit_height=false)=>{
  const [is_expanded, set_is_expanded] = useState(false);
  const [max_height, set_max_height] = useState<number>(value);
  const toggle = useCallback((v?: boolean)=>{
      if (v === undefined)
          set_is_expanded(prev=>!prev);
      else
          set_is_expanded(v);
  }, []);
  useEffect(()=>{
      if (ref.current)
      {
          const top_offset = ref.current.getBoundingClientRect().top;
          const available_height = window.innerHeight - top_offset;
          const calc_height = ref.current.scrollHeight + value + 100;
          set_max_height(is_expanded
              ? limit_height
              ? Math.min(calc_height, available_height)
              : calc_height
              : value);
      }
  }, [is_expanded, ref, value, render_key]);
  return {is_expanded, max_height, toggle};
};

export const IP_whitelist = ({zone_id})=>{
    const {theme} = useContext(Settings_context);
    const {user, dashboard} = use_app_context();
    const order = find_order_by_id(
        zone_id, dashboard?.info?.orders, dashboard?.info?.zone_groups);
    const wl = use_whitelist();
    const {ip, list, error, add_ip, remove_ip, paste_ip} = use_ip_list({
        initial_data: order?.ips_whitelist,
        on_add_ip: (added_ip, new_list)=>wl.add(added_ip, zone_id, new_list),
        on_remove_ip: (removed_ip, new_list)=>
            wl.remove(removed_ip, zone_id, new_list),
    });
    const {width} = use_screen();
    const is_mobile = width < 768;
    const find_myip = ()=>etask(function*(){
        const myip = yield get_myip();
        paste_ip(myip);
    });
    const form_ref = useRef<HTMLDivElement>();
    const list_ref = useRef<HTMLDivElement>();
    const ips = use_expand(list_ref, LIST_INIT_HEIGHT, list.length,
        !is_mobile);
    const ips_height = ips.is_expanded ? ips.max_height : 0;
    const form = use_expand(form_ref, FORM_INIT_HEIGHT + ips_height,
        list.length);
    const is_added = wl.state.data == WHITELIST_SUCCESS_CODES.ADDED;
    const is_updating_wl = wl.state.is_adding || wl.state.is_removing
        || is_added;
    useEffect(()=>{
        const need_refresh = [
            WHITELIST_SUCCESS_CODES.ADDED,
            WHITELIST_SUCCESS_CODES.REMOVED
        ].includes(wl.state.data);
        if (need_refresh)
            dashboard.dispatch({type: 'refresh_orders'});
    }, [wl.state]);
    useEffect(()=>{
        if (is_added)
            setTimeout(()=>{ wl.reset(); }, SUCCESS_MSG_TIMEOUT);
    }, [is_added]);
    if (!user || !order)
        return null;
    const success_msg = <div className={styles.success_msg}>
      <Icon_check />
      <p className={cn(is_mobile ? theme.p12 : theme.p14, theme.med,
        theme.color_success_bold)}>
        Success
      </p>
    </div>;
    const add_btn_caption = wl.state.is_adding
        ? <Btn_loader />
        : is_added
        ? success_msg
        : 'Add';
    const ips_cls = cn(styles.ips_list, {
        [styles.expanded]: ips.is_expanded,
    });
    const ips_style = {maxHeight: `${ips.max_height}px`};
    return <div className={cn(styles.ip_whitelist,
      form.is_expanded && styles.expanded, !!error && styles.error)}
      style={{maxHeight: `${form.max_height}px`}}>
      <div className={styles.root_title} onClick={()=>{
        ips.toggle(false);
        form.toggle();
      }}>
        <p className={cn(theme.p14, theme.med)}>Authenticated IP</p>
        <Icon_chevron_down />
      </div>
      <div className={cn(styles.add_form, form.is_expanded && styles.visible)}
        ref={form_ref}>
        <p className={cn(theme.p12, theme.color_n500)}>
          IP checking will provide another security layer to your proxies
        </p>
        <div className={styles.inputs}>
          <div className={cn(styles.segments, !!error && styles.error)}>
            {Array.from({length: 4}).map((_, i)=>{
              return <Fragment key={i}>
                <Segment_input ip={ip} index={i} cls={styles.segment_input} />
                {i < 3 && <p className={cn(theme.p14, theme.med,
                  theme.color_n400)}>.</p>}
              </Fragment>;
            })}
          </div>
          <Button caption={add_btn_caption} size='sm' fit={false}
            on_click={add_ip} disabled={is_updating_wl || !!error}
            cls={is_added ? styles.add_btn_success : null} />
        </div>
        {!!error && <div className={styles.error_msg}>
          <Icon_alert/>
          <p className={cn(theme.p12, theme.color_err_bold)}>
            {error_msg[error]}
          </p>
        </div>}
        <Button caption='Find my IP' color='secondary' size='xs'
          on_click={find_myip} />
        {list.length > 0 && <div className={ips_cls}>
          <div className={styles.divider} />
          <div className={styles.list_title}>
            <p className={theme.p14}>List of added IP's</p>
            <a className={cn(theme.P, theme.p14, theme.med, theme.color_n700,
              styles.h_s_toggler)} onClick={()=>ips.toggle()}>
              {ips.is_expanded ? 'Hide' : 'Show'}
            </a>
          </div>
          <div ref={list_ref} className={styles.list} style={ips_style}>
            {list.map(v=>{
              return <div key={v} className={styles.added_ip}>
                {wl.state.is_removing && wl.state.data == v
                  ? <div className={styles.remove_loader}><Btn_loader /></div>
                  : <>
                    <p className={theme.p14}>{v}</p>
                    {!is_updating_wl &&
                      <Icon_close onClick={()=>remove_ip(v)} />}
                  </>
                }
              </div>;
            })}
          </div>
        </div>}
      </div>
    </div>;
};
